import { Dialog, DialogProps, FormLayout, Heading, Paragraph } from '@expressable/syntax-ds';
import { yupResolver } from '@hookform/resolvers/yup';
import { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCognitoUserContext } from '../AWSCognitoWrapper/context';
import { LoginFormData } from '../LoginForm';
import { getLocalizedSchema } from '../LoginForm/schema';
import { createPortal } from 'react-dom';
import { FormUi } from '../LoginForm/formUi';
import { useTrack } from 'utils/hooks/useMixPanel';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';

export const SignupLoginModal: FC<DialogProps> = ({ ...dialogProps }) => {
  const { isLoggedIn } = useCognitoUserContext();

  const cognitoCtx = useCognitoUserContext();
  if (cognitoCtx.isLoggedIn) {
    throw new Error(`<Login/> component shouldn't render if there's an active session`);
  }

  const { logIn: onLogIn } = cognitoCtx;

  const { t } = useTranslation();

  const { track } = useTrack();

  const methods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(getLocalizedSchema(t)),
  });

  const {
    formState: { isSubmitting: isLoading },
    handleSubmit,
    setError,
  } = methods;

  const onSubmit = async ({ email, password }: LoginFormData) => {
    try {
      await onLogIn(email, password);
      track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CONTACT_CHECKER.TRIGGERED_LOGIN.SUCCESS);
    } catch {
      track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CONTACT_CHECKER.TRIGGERED_LOGIN.FAILURE);
      setError('root', {
        message: t('login.form.error'),
      });
    }
  };

  if (isLoggedIn) {
    return null;
  }

  return createPortal(
    <Dialog {...dialogProps} isDismissible={dialogProps.isDismissible && !isLoading} className="bg-cream-50">
      <div className="flex flex-col gap-e40 mobile-d:gap-e12 py-e24 px-e12">
        <div className="text-center max-w-[600px] mx-auto">
          <Heading level="h1">{t('directSignup.contactChecker.loginModal.title')}</Heading>
          <Paragraph size="lg" variant="primary">
            {t('directSignup.contactChecker.loginModal.subtitle')}
          </Paragraph>
        </div>
        <FormProvider {...methods}>
          <FormLayout
            onSubmit={e => {
              e.stopPropagation();
              handleSubmit(onSubmit)(e);
            }}
            className="max-w-[327px] mx-auto"
          >
            <FormUi isLoading={isLoading} showForgotPassword={false} />
          </FormLayout>
        </FormProvider>
      </div>
    </Dialog>,
    document.body,
  );
};
